import { defineTool, type ToolDefinition } from "./registry.ts";

export const build = defineTool<{ reconfigure?: boolean }>({
    name: "build",
    description:
        "Build the ESP-IDF project open in the idfweb tab, for the chip target or the CMake " +
        "preset selected there. " +
        "In a tree with several projects, the one select_project chose is built. " +
        "Unsaved changes in the tab's editor are not included. Returns whether the " +
        "build succeeded, the path of the ELF, the binaries a successful build made with " +
        "their sizes and the free space left in their partitions, and the output of each " +
        "failed step, which holds the compiler's errors. A build that failed outside a step, " +
        "such as in CMake's configure, returns the end of the build output instead. The " +
        "first build in a tab downloads the toolchain and takes minutes. The result gives " +
        "the target the build was for; " +
        "set_target changes it.",
    inputSchema: {
        type: "object",
        properties: {
            reconfigure: {
                type: "boolean",
                description:
                    "Run CMake's configure step again before building. Needed after changing " +
                    "CMakeLists.txt or sdkconfig.",
            },
        },
        additionalProperties: false,
    },
});

export const readFile = defineTool<{ path: string; offset?: number; limit?: number }>({
    name: "read_file",
    description:
        "Read a text file from the idfweb tab's filesystem. The opened folder or example is " +
        "the directory in /project named as it is, such as /project/blink, the build tree in " +
        "the project's build/ directory or its CMake preset's binaryDir, ESP-IDF under /idf " +
        "once the toolchain is downloaded. With offset or limit, only those lines are " +
        'returned, after a line such as "Lines 100-199 of 1234 of <path>:"; the text itself ' +
        "has no line numbers.",
    inputSchema: {
        type: "object",
        properties: {
            path: { type: "string", description: "Absolute path, e.g. /project/blink/main/main.c." },
            offset: {
                type: "integer",
                description: "The first line to return, counting from 1. Defaults to 1.",
            },
            limit: {
                type: "integer",
                description: "How many lines to return. Defaults to the rest of the file.",
            },
        },
        required: ["path"],
        additionalProperties: false,
    },
});

export const writeFile = defineTool<{ path: string; content: string }>({
    name: "write_file",
    description:
        "Create or replace a text file of the project open in the idfweb tab. Missing parent " +
        "directories are created. The editor shows the change, and a project opened from a local " +
        "folder gets it written back to that folder.",
    inputSchema: {
        type: "object",
        properties: {
            path: { type: "string", description: "Absolute path under /project." },
            content: { type: "string", description: "The whole new content of the file." },
        },
        required: ["path", "content"],
        additionalProperties: false,
    },
});

export const editFile = defineTool<{
    path: string;
    old_string: string;
    new_string: string;
    replace_all?: boolean;
}>({
    name: "edit_file",
    description:
        "Replace text in a file of the project open in the idfweb tab: old_string, which must " +
        "occur in the file exactly as given, including indentation, becomes new_string. If " +
        "old_string occurs more than once, the call fails and lists the lines where each " +
        "occurrence starts, unless replace_all is set. Returns the lines the new text is on in " +
        "the edited file. Edits run one after another in the order they arrive, so several " +
        "edits of a file can be sent at once. The file is saved as with write_file: a project " +
        "opened from a local folder gets it written back to that folder, and an editor tab " +
        "showing the file reloads it, except a tab with unsaved changes, which keeps them and " +
        "overwrites the edit when the user saves it.",
    inputSchema: {
        type: "object",
        properties: {
            path: { type: "string", description: "Absolute path under /project." },
            old_string: { type: "string", description: "The text to replace." },
            new_string: { type: "string", description: "The text to put in its place." },
            replace_all: {
                type: "boolean",
                description: "Replace every occurrence of old_string. Defaults to false.",
            },
        },
        required: ["path", "old_string", "new_string"],
        additionalProperties: false,
    },
});

export const listDirectory = defineTool<{ path: string }>({
    name: "list_directory",
    description: "List a directory of the idfweb tab's filesystem. Directory names end with a slash.",
    inputSchema: {
        type: "object",
        properties: {
            path: { type: "string", description: "Absolute path, e.g. /project." },
        },
        required: ["path"],
        additionalProperties: false,
    },
});

export const flash = defineTool<Record<string, never>>({
    name: "flash",
    description:
        "Write the last build's images to the board connected in the idfweb tab, then reset it. " +
        "Fails if no board is connected: the user has to press Connect in the tab and choose a " +
        "serial port or the emulator, because the browser opens the port picker only from a click.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
});

export const readSerial = defineTool<Record<string, never>>({
    name: "read_serial",
    description:
        "Return the serial output the connected board has sent since the previous read_serial " +
        "call or the start of the last flash, such as the boot log of the new firmware. Only " +
        "the last 64 KB are kept. Lines starting with --- are added by idfweb: after a " +
        "line with code addresses, their functions and source lines from the build's ELF file; " +
        "in place of a RISC-V panic's stack memory, the backtrace unwound from it. Bytes that " +
        "are not UTF-8 are written as \\xNN.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
});

export const monitor = defineTool<{ script: string; timeout_seconds?: number }>({
    name: "monitor",
    description:
        "Drive the board connected in the idfweb tab with a script, as esp-idf-monitor's " +
        "non-interactive mode does, and return what the board sent meanwhile. One command a " +
        "line; empty lines and lines starting with # are skipped. Commands: reset (reset the " +
        "board into its application); flash (as the flash tool); send <text> (write the text " +
        "and CR, the line end ESP-IDF's console reads, to the board); sleep <seconds>; " +
        "expect <regex> and expect --timeout <seconds> <regex> (wait for a line of output " +
        "that the JavaScript regular expression matches, so . + ( [ and the like need a \\ to " +
        "match themselves; an unfinished line such as a prompt is matched too; output that " +
        "read_serial or an earlier monitor call has not returned yet is searched first, up to " +
        "and including the match, except output from before a reset, a flash or a restart of " +
        "the board; to check for a prompt already shown, send an empty line first); " +
        "exit. The script stops at the first expect that times out, and the call fails. The " +
        "result holds the output not yet returned by read_serial and all output since, with " +
        "the same added lines as read_serial. Each command is marked by a line such as " +
        '"--- 1.25 s: send help" (time since the script started) at the first line end ' +
        "after it started, so a reply the script did not wait for can come after the next " +
        "command's line. When the board restarts by itself (a panic, a watchdog, esp_restart), " +
        "the result says so and a --- line marks where. The tab's serial pane shows the " +
        "commands as they run.",
    inputSchema: {
        type: "object",
        properties: {
            script: {
                type: "string",
                description: 'The commands, e.g. "reset\\nexpect --timeout 10 Returned from app_main".',
            },
            timeout_seconds: {
                type: "integer",
                description:
                    "Stop the script after this many seconds; an expect without --timeout waits " +
                    "at most until then. Defaults to 60, at most 600.",
            },
        },
        required: ["script"],
        additionalProperties: false,
    },
});

export const listProjects = defineTool<Record<string, never>>({
    name: "list_projects",
    description:
        "List the ESP-IDF projects under /project in the idfweb tab: directories whose " +
        "CMakeLists.txt includes ESP-IDF's tools/cmake/project.cmake or tools/cmakev2/idf.cmake, " +
        "such as the examples and test apps of a component. Marks the one builds use with its " +
        "chip target, and lists " +
        "its CMake presets if it has a CMakePresets.json. The tree is searched at each call, so " +
        "a project just written is listed.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
});

export const selectProject = defineTool<{ path: string; preset?: string }>({
    name: "select_project",
    description:
        "Choose the project directory that build configures, as idf.py -C does: its " +
        "CMakeLists.txt and sdkconfig are used and the build goes to its build/ directory. " +
        "The chip target becomes the one its sdkconfig sets, else its sdkconfig.defaults, if " +
        "either sets one; selecting the selected project again reads them again. The result " +
        "gives the target. If the build directory was configured for another target, the " +
        "next build runs as after set_target, so no reconfigure is needed. The directory's CMakeLists.txt must call project(); a component's " +
        "does not. A project with " +
        "a CMakePresets.json builds with a configure preset instead, as idf.py --preset does: " +
        "the build goes to the preset's binaryDir, and the target is the one the preset sets in " +
        "IDF_TARGET or its sdkconfig defaults, else the one selected in the tab.",
    inputSchema: {
        type: "object",
        properties: {
            path: {
                type: "string",
                description:
                    "Absolute path of a directory holding a CMakeLists.txt, " +
                    "e.g. /project/my_component/examples/basic.",
            },
            preset: {
                type: "string",
                description:
                    "Name of a configure preset of the project's CMakePresets.json or " +
                    "CMakeUserPresets.json. Without it, the preset chosen in the tab, else " +
                    '"default", else the first.',
            },
        },
        required: ["path"],
        additionalProperties: false,
    },
});

export const setTarget = defineTool<{ target: string }>({
    name: "set_target",
    description:
        "Change the chip target of the selected project, as idf.py set-target and the tab's " +
        "target dropdown do: the build directory is emptied now, and the next build moves " +
        "sdkconfig to sdkconfig.old and configures a new one for the target from " +
        "sdkconfig.defaults; if that configure fails, the old settings are in sdkconfig.old. " +
        "Until that build, the sdkconfig still holds the old target, and " +
        "select_project on the project would take that one again. A CMake preset that sets " +
        "its target fixes it; choose another preset with select_project instead.",
    inputSchema: {
        type: "object",
        properties: {
            target: { type: "string", description: "ESP-IDF target name, e.g. esp32s3." },
        },
        required: ["target"],
        additionalProperties: false,
    },
});

export const runCommand = defineTool<{ command: string; cwd?: string; timeout_seconds?: number }>({
    name: "run_command",
    description:
        "Run a shell command in the idfweb tab's filesystem and return its exit status, stdout " +
        "and stderr. The shell is BusyBox hush: pipes, redirections, $(...), variables, loops " +
        "and functions work. Available commands: grep, sed, awk, find, xargs, head, tail, wc, " +
        "sort, uniq, cut, tr, diff, patch, cat, ls, cp, mv, rm, mkdir and other BusyBox " +
        "applets, plus clang, clang++, ld.lld, llvm-ar, cmake, ninja and ice, which provides " +
        "ESP-IDF's Python tools. python3 runs ice, which runs a script it has no equivalent of in " +
        "CPython 3.14 (python3.14): the standard library and pure-Python packages from ESP-IDF's " +
        "requirements such as rich-click, PyYAML and pyelftools; no pip, threads or subprocess. For " +
        "object files and ELF images there are llvm-objdump, llvm-readelf, llvm-readobj, " +
        "llvm-nm and llvm-size, which take the GNU tools' options; to disassemble Xtensa code " +
        "give llvm-objdump the chip, as in --mcpu=esp32s3. A command cut short by a pipe into " +
        'head prints "Program recieved termination signal: Broken pipe", which is harmless. The ' +
        "build's compiler flags are in compile_commands.json in the build directory of the " +
        "selected project (see select_project). The first command in a tab downloads the " +
        "toolchain. Runs after any build in progress.",
    inputSchema: {
        type: "object",
        properties: {
            command: { type: "string", description: "The command line, as for sh -c." },
            cwd: {
                type: "string",
                description: "Absolute working directory. Defaults to the selected project.",
            },
            timeout_seconds: {
                type: "integer",
                description: "Stop the command after this many seconds. Defaults to 60, at most 600.",
            },
        },
        required: ["command"],
        additionalProperties: false,
    },
});

export const listControls = defineTool<Record<string, never>>({
    name: "list_controls",
    description:
        "List the controls of the idfweb tab that show_control can point the user at, one per " +
        'line as <role> "<name>", such as button "Build", tab "Serial Monitor" or ' +
        'treeitem "main.c". The list is read from the page now, so it holds the explorer\'s ' +
        "visible files, the open editor tabs and, while the sdkconfig editor is open, the " +
        "settings it has drawn, which are those near its scroll position; show_config_option " +
        "finds any option. A control in the hidden left side bar or right-hand column is listed too; " +
        "show_control opens that part first.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
});

export const showControl = defineTool<{ role: string; name: string; text?: string; wait?: boolean }>({
    name: "show_control",
    description:
        "Point the user at a control of the idfweb tab: the page is dimmed around it and a card " +
        "next to it shows the text. The control is found by its role and accessible name, as " +
        "list_controls gives them; the name also matches the start of a control's name, " +
        "ignoring case. A control in a hidden part of the page is shown first, and a tab is " +
        "selected. The card closes when the user clicks anything, presses Escape or the card's " +
        "button, and when another card is shown. With wait, the call returns when the card " +
        "closes and says how, so the next step can follow the user's.",
    inputSchema: {
        type: "object",
        properties: {
            role: {
                type: "string",
                description: "One of button, link, tab, combobox, textbox, checkbox, treeitem, menuitem, heading.",
            },
            name: { type: "string", description: "The control's name, e.g. Build." },
            text: {
                type: "string",
                description:
                    "What to tell the user, in Markdown without block elements: `code`, " +
                    "**bold**, links. Keep it to a sentence or two.",
            },
            wait: {
                type: "boolean",
                description:
                    "Return only when the card closes, which can take as long as the user takes. Defaults to false.",
            },
        },
        required: ["role", "name"],
        additionalProperties: false,
    },
});

export const showLines = defineTool<{ path: string; lines: string; note?: string }>({
    name: "show_lines",
    description:
        "Show the user lines of a file in the idfweb tab's editor: the file opens in a tab, the " +
        "lines are selected and scrolled into view, and the note is shown under them until the " +
        "user edits the file or closes the note. Opening another file's lines this way removes " +
        "the note from the first. Use it to point at the code a change goes into, or to explain " +
        "what a part of a file does.",
    inputSchema: {
        type: "object",
        properties: {
            path: { type: "string", description: "Absolute path, e.g. /project/blink/main/main.c." },
            lines: {
                type: "string",
                description:
                    "The lines, as <start>:<end> or a single <start>. Each side is a line number, " +
                    "a /glob/ or an r/regex/ matched anywhere in a line; the end is searched for " +
                    "after the start and is excluded unless it ends with +, e.g. 12:20 or " +
                    "r/^void app_main/:r/^\\}/+. A colon inside a pattern is written \\:.",
            },
            note: {
                type: "string",
                description: "What to tell the user about the lines, in Markdown without block elements.",
            },
        },
        required: ["path", "lines"],
        additionalProperties: false,
    },
});

export const runUiCommand = defineTool<{ id?: string }>({
    name: "run_ui_command",
    description:
        "Run a command of the idfweb tab's command palette as if the user chose it, such as " +
        "workbench.action.showCommands (open the palette), workbench.action.quickOpen (Go to " +
        "File), workbench.action.findInFiles, workbench.action.openSettings or " +
        "workbench.action.toggleSidebarVisibility. Without an id, lists the commands the palette " +
        "offers now, with their titles and keys. For builds, use the build tool, which returns " +
        "the result.",
    inputSchema: {
        type: "object",
        properties: {
            id: { type: "string", description: "The command's id, e.g. workbench.action.showCommands." },
        },
        additionalProperties: false,
    },
});

export const setLayout = defineTool<{ preset: string; path?: string }>({
    name: "set_layout",
    description:
        "Arrange the idfweb tab for what the user does next, and keep it as the project's layout. " +
        "Presets: reading (the editor alone), editing (the explorer and the editor), monitor " +
        "(the editor and the serial monitor), output (the editor and the build output), all " +
        "(the explorer, the editor and the output panel). With a path, that file is opened and " +
        "becomes the editor's active tab; a Markdown file opens rendered. On a narrow screen the " +
        "explorer is a drawer and stays as it is.",
    inputSchema: {
        type: "object",
        properties: {
            preset: { type: "string", description: "reading, editing, monitor, output or all." },
            path: { type: "string", description: "Absolute path of a file to show, e.g. /project/blink/README.md." },
        },
        required: ["preset"],
        additionalProperties: false,
    },
});

export const setConfig = defineTool<{ settings: string }>({
    name: "set_config",
    description:
        "Change the project configuration (sdkconfig) as the tab's sdkconfig editor and " +
        "idf.py menuconfig do: ESP-IDF's configuration server applies each value, checks it " +
        "against the Kconfig dependencies and ranges, updates the options that depend on it, " +
        "and saves the sdkconfig. Returns each option's new value, the values of other options " +
        "the change moved, and the options that did not take a value, such as one hidden " +
        "because another option it depends on is off. If the sdkconfig editor is open, the user " +
        "sees the change there; show_config_option points the user at an option in it. A project " +
        "no build has configured yet is configured first: CMake's configure step alone, without " +
        "a compile, after the toolchain download if the tab has none yet. The next build picks the " +
        "change up; no reconfigure is needed. To choose one option of a choice, set that option " +
        "to y. The values are saved in sdkconfig only: set_target, or deleting sdkconfig, starts " +
        "again from sdkconfig.defaults, so add the lines there too to keep them.",
    inputSchema: {
        type: "object",
        properties: {
            settings: {
                type: "string",
                description:
                    "Lines in sdkconfig format, applied in one request: CONFIG_NAME=y, " +
                    "CONFIG_NAME=n or # CONFIG_NAME is not set, CONFIG_NAME=123, " +
                    'CONFIG_NAME=0x1000, CONFIG_NAME="text". The CONFIG_ prefix may be left out.',
            },
        },
        required: ["settings"],
        additionalProperties: false,
    },
});

export const showConfigOption = defineTool<{ name: string; text?: string; path?: string; wait?: boolean }>({
    name: "show_config_option",
    description:
        "Point the user at a Kconfig option in the idfweb tab's sdkconfig editor, the form in " +
        "which the user changes the project configuration as in menuconfig: the sdkconfig opens " +
        "in the editor, the form is searched for the option, and the page is dimmed around its " +
        "row with the text in a card next to it. An option of a choice shows the choice's row. " +
        "Fails for an option hidden by its dependencies. A project no build has configured yet is " +
        "configured first, as set_config does, which creates the sdkconfig. With wait, the call " +
        "returns when the card closes and says how.",
    inputSchema: {
        type: "object",
        properties: {
            name: { type: "string", description: "The option, with or without CONFIG_, e.g. LOG_DEFAULT_LEVEL_DEBUG." },
            text: {
                type: "string",
                description: "What to tell the user, in Markdown without block elements.",
            },
            path: {
                type: "string",
                description: "The sdkconfig to open. Defaults to the one in the selected project's directory.",
            },
            wait: {
                type: "boolean",
                description:
                    "Return only when the card closes, which can take as long as the user takes. Defaults to false.",
            },
        },
        required: ["name"],
        additionalProperties: false,
    },
});

/**
 * The events wait_for waits for, with what each means to an agent. The tool's
 * description and its `event` argument are made from this list, and the
 * page's `PageEvent` has one variant for each name.
 */
export const WAIT_EVENTS = {
    build: "a build ended, the user's or an agent's; says whether it succeeded and for which chip, and why not",
    flash: "a flash ended, written or failed; the toolbar's Run builds and then flashes",
    target: "the chip target changed, in the toolbar's dropdown or to a detected board's chip; list_projects gives the current one",
    board: "a board is connected and ready; one that already is counts at once",
    command:
        "a command started from the palette, a key, a toolbar button such as Build (idfweb.build) or Run (idfweb.run), or run_ui_command, filtered by id; choosing a chip or connecting a board is not a command",
    palette: 'the quick input opened, filtered by prefix: > is the command palette, "" Go to File',
    saved: "the editor saved a file, also by auto-save about a second after typing stops, filtered by path",
    serial: "a line of the board's output matched pattern",
} as const;

export type WaitEvent = keyof typeof WAIT_EVENTS;

const waitEventNames = Object.keys(WAIT_EVENTS);

export const waitFor = defineTool<{
    event: string;
    id?: string;
    prefix?: string;
    path?: string;
    pattern?: string;
    timeout_seconds?: number;
}>({
    name: "wait_for",
    description:
        "Wait until something happens in the idfweb tab, so that after asking the user to do a " +
        "step, such as pressing Build after show_control, the next step follows theirs. Events: " +
        Object.entries(WAIT_EVENTS)
            .map(([name, meaning]) => `${name} (${meaning})`)
            .join(", ") +
        ". An event counts from the start of your previous call to this tab, so one that came " +
        "while you were between calls is not missed, and an event this tool returned once is " +
        "not returned again. Returns what happened, or that nothing did within the timeout. A " +
        "failed build's errors are in the build tool's result, not here.",
    inputSchema: {
        type: "object",
        properties: {
            event: {
                type: "string",
                description: `${waitEventNames.slice(0, -1).join(", ")} or ${waitEventNames.at(-1)}.`,
            },
            id: { type: "string", description: "For command: the command's id, e.g. idfweb.build." },
            prefix: { type: "string", description: 'For palette: the prefix, e.g. ">".' },
            path: {
                type: "string",
                description: "For saved: the file's absolute path, e.g. /project/blink/main/main.c.",
            },
            pattern: {
                type: "string",
                description:
                    "For serial, required: a regex searched for in each line, without the line end; " +
                    "a leading (?i) ignores case.",
            },
            timeout_seconds: {
                type: "integer",
                description: "How long to wait, up to 1800. Defaults to 300.",
            },
        },
        required: ["event"],
        additionalProperties: false,
    },
});

export const CATALOG: ToolDefinition[] = [
    build,
    readFile,
    writeFile,
    editFile,
    listDirectory,
    flash,
    readSerial,
    monitor,
    listProjects,
    selectProject,
    setTarget,
    runCommand,
    listControls,
    showControl,
    showLines,
    runUiCommand,
    setLayout,
    setConfig,
    showConfigOption,
    waitFor,
];
